/**
 * T2-6151 Message Users — ID-list audience safety helpers.
 * When an uploaded file has zero valid IDs (or a parse error), Preview/Send
 * must not proceed with roles/provinces alone (that silently widens audience).
 */

export type IdListParseSummary = {
  validCount: number;
  validIds?: number[];
} | null;

export function isIdListAudienceBlocked(params: {
  idListError?: string | null;
  idListFile?: File | null;
  idListResult?: IdListParseSummary;
  idListParsing?: boolean;
}): boolean {
  const { idListError, idListFile, idListResult, idListParsing } = params;
  if (idListParsing) return true;
  if (idListError && String(idListError).trim()) return true;
  if (idListFile && (!idListResult || idListResult.validCount <= 0)) return true;
  return false;
}

export function buildMessageUsersAudience(params: {
  roles: number[];
  provinces: string[];
  idListType: "hygienist" | "dentist";
  idListResult?: IdListParseSummary;
}): {
  roles: number[];
  provinces: string[];
  idList?: { type: "hygienist" | "dentist"; ids: number[] };
} {
  const audience: {
    roles: number[];
    provinces: string[];
    idList?: { type: "hygienist" | "dentist"; ids: number[] };
  } = {
    roles: params.roles,
    provinces: params.provinces
  };
  const ids = params.idListResult?.validIds;
  if (params.idListResult && params.idListResult.validCount > 0 && ids && ids.length > 0) {
    audience.idList = {
      type: params.idListType,
      ids: [...ids]
    };
  }
  return audience;
}

/** Stable identity for the recipient filters, independent of selection order. */
export function messageUsersAudienceFingerprint(audience: {
  roles: number[];
  provinces?: string[];
  countries?: number[];
  idList?: { type: "hygienist" | "dentist"; ids: number[] };
}): string {
  const numbers = (values: number[] = []) =>
    [...new Set(values.map(Number))].sort((a, b) => a - b);
  const strings = (values: string[] = []) =>
    [...new Set(values.map(String))].sort();
  return JSON.stringify({
    roles: numbers(audience.roles),
    provinces: strings(audience.provinces),
    countries: numbers(audience.countries),
    idList: audience.idList
      ? {
          type: audience.idList.type,
          ids: numbers(audience.idList.ids)
        }
      : null
  });
}
